import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultSitesExportFieldNames, sitesExportFields } from "~pages/Sites/SiteListPage/constants/exportFields.ts";
import { useDeleteSite } from "~pages/Sites/SiteListPage/hooks/useDeleteSite.ts";
import { useExportSites } from "~pages/Sites/SiteListPage/hooks/useExportSites.ts";
import { useFetchSites } from "~pages/Sites/SiteListPage/hooks/useFetchSites.ts";
import { useGetSiteTableColumns } from "~pages/Sites/SiteListPage/hooks/useGetSiteTableColumns.tsx";
import { useGetSiteTableFilters } from "~pages/Sites/SiteListPage/hooks/useGetSiteTableFilters.tsx";
import { useImportSites } from "~pages/Sites/SiteListPage/hooks/useImportSites.ts";

const SiteListPage = () => {
  const paginationConfig = useHandlePagination(20, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const { mutateAsync: startImporting } = useImportSites();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportSites();
  const location = useLocation();
  const navigate = useNavigate();
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/sites/manage/`);
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [sortBy, setSortBy] = useState<string | null>(null);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.sites.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.sites.listPage.importConfig;
  const filterConfig = useGetSiteTableFilters();

  const { data, refetch, isPending } = useFetchSites({
    enabled: sortBy !== null,
    customParams: {
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      countryCode: filterConfig.currentFilter.countryCode?.value,
      nameLocalContains: searchText,
    },
  });

  const { mutateAsync: deleteFn, isPending: isDeletePending } = useDeleteSite();

  const handleDelete = useCallback(
    async (siteId: string) => {
      return await deleteFn(siteId);
    },
    [deleteFn],
  );

  const columns = useGetSiteTableColumns();

  const dataSource = data?.data || [];

  const getNavigateUrl = (siteId: string) => `/${customerId}/admin/${sport}/${organizationId}/sites/manage/${siteId}`;

  return (
    <DataTable
      handleRefresh={() => {
        refetch();
      }}
      title={formatMessage({ id: "siteListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.siteId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.siteId)),
          },
        },
      }}
      rowKey={"siteId"}
      rowNameKey={"nameLocal"}
      dataSource={dataSource}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.sites`}
      isDataLoading={isPending}
      handleAdd={handleAdd}
      addButtonIntlKey={"site"}
      filtersConfig={filterConfig}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.sites`,
        defaultSorter: {
          columnKey: "nameLocal",
          order: "descend",
        },
        onSortChange: setSortBy,
      }}
      exportConfig={{
        handleExport: startExport,
        loading: isExportPending,
        defaultFields: defaultSitesExportFieldNames,
        groupedFields: sitesExportFields,
        localStorageKey: "export.sites",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: startImporting,
      }}
      permissionNames={[PermissionNames.SITE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default SiteListPage;
